import { useState, useEffect, useCallback } from 'react';
import './QuoteRequest.css';
import CallSimulator from './CallSimulator';
import { getDemoCallExamples, isDemoApiKey } from '../services/DemoUser';
import GeminiService from '../services/GeminiService';
import SpeechToTextService from '../services/SpeechToTextService';

const QuoteRequest = ({ userConfig, onCallComplete }) => {
  const [prompt, setPrompt] = useState(userConfig.defaultPrompt || '');
  const [isCallActive, setIsCallActive] = useState(false);
  const [parsedInfo, setParsedInfo] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [isPolishing, setIsPolishing] = useState(false);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [speechError, setSpeechError] = useState('');

  // Analyze the default prompt if it exists
  useEffect(() => {
    if (userConfig.defaultPrompt) {
      analyzePrompt(userConfig.defaultPrompt);
    }
  }, [userConfig.defaultPrompt]);

  // Memoized polishTranscription function
  const polishTranscription = useCallback(async (rawText) => {
    try {
      setIsPolishing(true);
      setIsRecording(false);
      setLiveTranscript('');
      
      console.log('🎤 Polishing voice transcription with AI...');
      
      const polishingPrompt = `Polish this voice transcription into a clear, professional delegation request. The user wants to delegate a task involving calling a business for a quote or service. 

Add specific details that would be helpful for making the call:
- If no business name is mentioned, suggest they add one
- If no phone number, suggest they include it
- If location is vague, suggest being more specific
- Include relevant project details
- Make it sound professional and complete

Raw transcription: "${rawText}"

Return a polished, complete delegation request:`;

      const polished = await GeminiService.generateResponse(polishingPrompt, []);
      console.log('✨ Voice transcription polished successfully');
      
      // Set the polished text and analyze it
      setPrompt(polished);
      analyzePrompt(polished);
      
    } catch (error) {
      console.error('Error polishing transcription:', error);
      // If polishing fails, use the raw transcription
      setPrompt(rawText);
      analyzePrompt(rawText);
    } finally {
      setIsPolishing(false);
    }
  }, []);

  // Set up speech recognition
  useEffect(() => {
    if (!SpeechToTextService.isSupported()) {
      setSpeechError('Speech recognition is not supported in this browser');
      return;
    }

    // Set up callbacks
    SpeechToTextService.onResult((result) => {
      setLiveTranscript(result.combined);
    });

    SpeechToTextService.onEnd((finalTranscript) => {
      if (finalTranscript.trim()) {
        polishTranscription(finalTranscript);
      } else {
        setIsRecording(false);
        setLiveTranscript('');
        setSpeechError('No speech detected. Please try again.');
      }
    });

    SpeechToTextService.onError((error) => {
      setSpeechError(error);
      setIsRecording(false);
      setLiveTranscript('');
    });

    // Cleanup on unmount
    return () => {
      SpeechToTextService.destroy();
    };
  }, [polishTranscription]);

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
    analyzePrompt(e.target.value);
  };

  const analyzePrompt = (text) => {
    const info = {};
    
    // Basic parsing logic
    const phoneRegex = /(\(?[\d\s\-.()\s]{10,}\)?)/g;
    const phones = text.match(phoneRegex);
    if (phones) info.businessPhone = phones[0].replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');

    // Look for business names (capitalized words)
    const businessRegex = /\b([A-Z][a-z]*\s*[A-Z][a-z]*(?:\s+[A-Z][a-z]*)*)\b/g;
    const businesses = text.match(businessRegex);
    if (businesses) info.businessName = businesses[0];

    // Look for addresses
    const addressRegex = /\d+\s+[A-Za-z\s,]+(?:street|st|avenue|ave|road|rd|drive|dr|lane|ln|boulevard|blvd|way|place|pl)/i;
    const address = text.match(addressRegex);
    if (address) info.propertyAddress = address[0];

    // Service type detection
    const services = ['painting', 'plumbing', 'electrical', 'landscaping', 'cleaning', 'roofing', 'hvac', 'flooring', 'handyman'];
    for (const service of services) {
      if (text.toLowerCase().includes(service)) {
        info.serviceType = service;
        break;
      }
    }

    setParsedInfo(info);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsCallActive(true);
  };

  const handleCallComplete = (callResult) => {
    setIsCallActive(false);
    onCallComplete({
      id: Date.now(),
      timestamp: new Date().toISOString(),
      originalPrompt: prompt,
      serviceType: parsedInfo.serviceType || 'service request',
      businessName: parsedInfo.businessName || 'Business',
      businessPhone: parsedInfo.businessPhone || 'Unknown',
      status: callResult.status,
      transcript: callResult.transcript,
      outcome: callResult.outcome,
      quote: callResult.quote
    });
    
    setPrompt('');
    setParsedInfo({});
  };

  const getMissingInfo = () => {
    const missing = [];
    if (!parsedInfo.businessName) missing.push('business name');
    if (!parsedInfo.businessPhone) missing.push('phone number');
    if (!parsedInfo.serviceType) missing.push('service type');
    if (!parsedInfo.propertyAddress) missing.push('property address');
    return missing;
  };

  const isFormValid = () => {
    return prompt.length > 20 && parsedInfo.businessPhone;
  };

  if (isCallActive) {
    return (
      <CallSimulator
        userConfig={userConfig}
        callData={{
          businessName: parsedInfo.businessName || 'Business',
          businessPhone: parsedInfo.businessPhone || 'Unknown',
          serviceType: parsedInfo.serviceType || 'service request',
          projectDescription: prompt,
          propertyAddress: parsedInfo.propertyAddress || 'property address'
        }}
        onCallComplete={handleCallComplete}
      />
    );
  }

  const missingInfo = getMissingInfo();
  const isDemo = isDemoApiKey(userConfig.apiKey);
  const demoExamples = getDemoCallExamples();

  const handleExampleClick = (example) => {
    setPrompt(example.prompt);
    analyzePrompt(example.prompt);
  };

  const startRecording = async () => {
    try {
      setSpeechError('');
      setLiveTranscript('');
      
      // Provide haptic feedback on mobile devices
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      setIsRecording(true);
      await SpeechToTextService.startListening();
    } catch (error) {
      console.error('Error starting speech recognition:', error);
      setSpeechError('Could not start speech recognition. Please check permissions.');
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    // Provide haptic feedback on mobile devices
    if (navigator.vibrate) {
      navigator.vibrate([50, 50, 50]);
    }
    
    SpeechToTextService.stopListening();
    setIsRecording(false);
  };



  return (
    <div className="quote-request">
      <h3>What do you want to delegate?</h3>
      <p>Describe what you need help with, and our AI will call to handle it for you.</p>
      
      {isDemo && (
        <div className="demo-examples">
          <h4>🎯 Try These Demo Examples</h4>
          <div className="example-grid">
            {demoExamples.map((example, index) => (
              <div 
                key={index} 
                className="example-card"
                onClick={() => handleExampleClick(example)}
              >
                <h5>{example.title}</h5>
                <p>{example.prompt.substring(0, 100)}...</p>
                <span className="try-example">Click to try →</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="quote-form">
        <div className="form-group">
          <div className="prompt-container">
            <textarea
              id="prompt"
              name="prompt"
              value={prompt}
              onChange={handlePromptChange}
              placeholder="Example: I need ABC Painting Company (555-123-4567) to give me a quote for painting my living room and kitchen at 123 Main St. The rooms are about 400 sq ft total and I'd like to use Benjamin Moore paint."
              rows="6"
              className="main-prompt"
              disabled={isPolishing}
            />
            <button
              type="button"
              className={`mic-button ${isRecording ? 'recording' : ''} ${isPolishing ? 'polishing' : ''} ${!SpeechToTextService.isSupported() ? 'disabled' : ''}`}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isPolishing || !SpeechToTextService.isSupported()}
              title={
                !SpeechToTextService.isSupported() ? 'Speech recognition not supported in this browser' :
                isRecording ? 'Stop recording' : 
                isPolishing ? 'Processing...' : 
                'Start voice input'
              }
            >
              {isPolishing ? (
                <span className="loading-dots">⚡</span>
              ) : isRecording ? (
                <span className="recording-icon">⏹️</span>
              ) : (
                <span className="mic-icon">🎤</span>
              )}
            </button>
          </div>
          {isRecording && liveTranscript && (
            <div className="live-transcript">
              <span className="transcript-label">Live transcript:</span>
              <span className="transcript-text">"{liveTranscript}"</span>
            </div>
          )}
          {isPolishing && (
            <div className="polishing-status">
              <span className="polishing-text">✨ AI is polishing your voice input...</span>
            </div>
          )}
          {!SpeechToTextService.isSupported() && (
            <div className="speech-not-supported">
              <span className="info-text">ℹ️ Voice input not supported in this browser</span>
              <div className="browser-recommendations">
                <p><strong>Try using:</strong></p>
                <ul>
                  <li>Chrome (recommended)</li>
                  <li>Edge</li>
                  <li>Safari (iOS/macOS)</li>
                </ul>
              </div>
            </div>
          )}
          {speechError && (
            <div className="speech-error">
              <span className="error-text">⚠️ {speechError}</span>
              {/Microphone.*blocked|not-allowed/i.test(speechError) && (
                <div className="mobile-instructions">
                  <p><strong>Mobile users:</strong></p>
                  <ol>
                    <li>Tap the address bar</li>
                    <li>Look for the microphone icon 🎤</li>
                    <li>Tap "Allow" or enable permissions</li>
                    <li>Refresh the page and try again</li>
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>

        {prompt.length > 0 && (
          <div className="parsing-feedback">
            <h4>Information Detected:</h4>
            <div className="detected-info">
              {parsedInfo.businessName && (
                <span className="info-chip success">✓ Business: {parsedInfo.businessName}</span>
              )}
              {parsedInfo.businessPhone && (
                <span className="info-chip success">✓ Phone: {parsedInfo.businessPhone}</span>
              )}
              {parsedInfo.serviceType && (
                <span className="info-chip success">✓ Service: {parsedInfo.serviceType}</span>
              )}
              {parsedInfo.propertyAddress && (
                <span className="info-chip success">✓ Address: {parsedInfo.propertyAddress}</span>
              )}
            </div>
            
            {missingInfo.length > 0 && (
              <div className="missing-info">
                <p><strong>Please add:</strong> {missingInfo.join(', ')}</p>
                <div className="guidance">
                  <p><strong>Tips:</strong></p>
                  <ul>
                    {!parsedInfo.businessName && <li>Include the business name (e.g., "ABC Company")</li>}
                    {!parsedInfo.businessPhone && <li>Include the phone number (e.g., "555-123-4567")</li>}
                    {!parsedInfo.serviceType && <li>Mention the service type (painting, plumbing, electrical, etc.)</li>}
                    {!parsedInfo.propertyAddress && <li>Include the property address with street number</li>}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {prompt.length > 0 && (
          <div className="call-preview">
            <h4>Call Preview</h4>
            <p>Our AI will call <strong>{parsedInfo.businessName || '[Business Name]'}</strong> and say:</p>
            <div className="preview-text">
              "This is an AI voice call on behalf of {userConfig.name}. To stop transcriptions from being recorded, please press 1. 
              I'm calling to request information about {parsedInfo.serviceType || 'services'}. {prompt.substring(0, 150)}{prompt.length > 150 ? '...' : ''}"
            </div>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-primary btn-large"
          disabled={!isFormValid()}
        >
          {!isFormValid() ? 
            `Make Call ${!parsedInfo.businessPhone ? '(need phone number)' : '(need more details)'}` : 
            'Make Call & Get Quote'
          }
        </button>
      </form>
    </div>
  );
};

export default QuoteRequest;